import React from "react";
import { useLocation, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { Bot, ArrowUpRight, Hammer } from "lucide-react";

const MODULES = {
  "/culture": { title: "Culture Builder", tag: "Module 04", desc: "Quote library, weekly themes, leadership curriculum, character lessons, guest speaker notes.",
    features: ["Quote Library", "Character Lessons", "Weekly Themes", "Team Building", "Book Recommendations", "Daily Challenge Generator"] },
  "/drill-library": { title: "Drill Library", tag: "Module 06", desc: "Unlimited drills tagged by position, difficulty, and category — with video, diagrams, coaching points.",
    features: ["Position tags", "Video attachments", "Coaching points", "Common mistakes", "Favorites & search"] },
  "/playbook": { title: "Playbook", tag: "Module 07", desc: "Offense, Defense, ST — with formation, personnel, install date, video, and situational filters.",
    features: ["Formation & personnel", "Install date tracking", "Situational: goal line / 2-min / 4-min", "Diagram + video per play"] },
  "/film-hub": { title: "Film Hub", tag: "Module 08", desc: "Hudl link hub. Categorize playlists, link film to plays and drills, AI clip summaries.",
    features: ["Hudl link storage", "Playlist categorization", "Play/drill linking", "Staff & player comments", "AI clip summary"] },
  "/player-development": { title: "Player Development", tag: "Module 09", desc: "Track attendance, weight room, speed, grades, character, leadership across the roster.",
    features: ["Attendance tracking", "Strength metrics", "Character/leadership grades", "Season progress graphs"] },
  "/calendar": { title: "Calendar", tag: "Module 11", desc: "Practices, games, meetings, weight room, meals, travel — sync Google & Outlook.",
    features: ["Multi-source sync", "Dead-period awareness", "Recruiting events", "Team & staff calendars"] },
  "/documents": { title: "Documents", tag: "Module 12", desc: "Unlimited folders. PDFs, playbooks, forms, images, videos — with versioning and permissions.",
    features: ["Unlimited folders", "Version history", "Role-based permissions", "Universal search"] },
  "/messaging": { title: "Messaging", tag: "Module 13", desc: "Staff, position groups, players, parents. Broadcasts, read receipts, pinned messages.",
    features: ["Position group chats", "Parent broadcasts", "Read receipts", "Pinned announcements"] },
  "/knowledge-base": { title: "AI Knowledge Base", tag: "Module 14", desc: "Program-aware AI that knows your mission, drills, and terminology. See the AI Football Coach.",
    features: ["Program document embedding", "RAG over playbook", "Terminology-aware search"], link: "/ai-coach", linkLabel: "Open AI Coach" },
};

export default function ModulePlaceholder() {
  const loc = useLocation();
  const meta = MODULES[loc.pathname] || { title: "Module", tag: "Coming Soon", desc: "This module is on the roadmap.", features: [] };

  return (
    <div data-testid="module-placeholder">
      <TopBar title={meta.title} subtitle={meta.tag + " · Coming Soon"} />
      <div className="p-8 max-w-4xl">
        <div className="border border-surface-3 bg-surface relative overflow-hidden">
          <div className="badge-stripe h-1" />
          <div className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 border border-amber/40 bg-amber/10 flex items-center justify-center shrink-0">
                <Hammer className="w-6 h-6 text-amber" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-condensed uppercase tracking-[0.3em] text-amber">{meta.tag}</div>
                <div className="font-display text-4xl uppercase tracking-wide text-white mt-1 leading-tight">{meta.title}</div>
                <p className="text-slate-300 mt-3 leading-relaxed max-w-2xl">{meta.desc}</p>
              </div>
            </div>
            <div className="hairline my-6" />
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mb-3">Planned Features</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {meta.features.map((f) => (
                <div key={f} className="flex items-center gap-3 px-3 py-2 border border-surface-3 bg-background">
                  <div className="w-1.5 h-1.5 bg-amber" />
                  <div className="text-sm text-slate-200">{f}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-3">
              <Link
                to={meta.link || "/ai-coach"}
                className="inline-flex items-center gap-2 px-5 py-3 bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-[0.25em] text-xs transition-colors"
                data-testid="module-cta"
              >
                <Bot className="w-4 h-4" /> {meta.linkLabel || "Try the AI Coach"} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/" className="text-xs font-condensed uppercase tracking-widest text-slate-500 hover:text-amber">
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
